import { apiClient } from '@/lib/auth/api-client';
import { captureFrontendError } from '@/lib/errors/error-context';

export const ACTIVITIES_NAV_COUNT_KEY = 'activities';

const ORIGIN = 'activities-nav-count';

type CountRead = () => Promise<
  { readonly ok: true; readonly body: { readonly count: number } } | { readonly ok: false; readonly status: number }
>;

function report(source: 'store' | 'lead', reported: Set<string>, cause: unknown, status?: number): void {
  const key = `${source}:${status ?? 'network'}`;

  if (reported.has(key)) return;

  reported.add(key);
  captureFrontendError(cause, ORIGIN, { source, ...(status === undefined ? {} : { status }) });
}

async function readCount(
  source: 'store' | 'lead',
  reported: Set<string>,
  read: CountRead,
  label: string,
): Promise<number | undefined> {
  try {
    const result = await read();

    if (result.ok) return result.body.count;

    report(source, reported, new Error(`${label} answered ${result.status}`), result.status);
  } catch (error) {
    report(source, reported, error);
  }

  return undefined;
}

/**
 * The two count reads, summed; undefined when either failed — a partial sum is a number known to be
 * wrong. A failure is captured once per source and status for the `reported` set the caller owns.
 */
export async function fetchActivitiesOwnOpenCount(
  workspaceId: string,
  reported: Set<string>,
): Promise<number | undefined> {
  const [store, lead] = await Promise.all([
    readCount(
      'store',
      reported,
      () => apiClient.activities.ownOpenCount({ pathParams: { workspaceId } }),
      'activities.ownOpenCount',
    ),
    readCount(
      'lead',
      reported,
      () => apiClient.leads.activityOwnOpenCount({ pathParams: { workspaceId } }),
      'leads.activityOwnOpenCount',
    ),
  ]);

  return store === undefined || lead === undefined ? undefined : store + lead;
}
